import { useEffect } from 'react';
import { SITE_NAME, SITE_LOGO, absoluteUrl } from '@/seo/site';
import { routeSeo, notFoundSeo } from '@/seo/seoConfig';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setRobots(content: string | null) {
  const el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (content === null) {
    if (el) el.remove();
    return;
  }
  const target =
    el ||
    (() => {
      const created = document.createElement('meta');
      created.setAttribute('name', 'robots');
      created.setAttribute('data-seo-managed', 'true');
      document.head.appendChild(created);
      return created;
    })();
  target.setAttribute('content', content);
}

interface ApplyOptions {
  title: string;
  description: string;
  image: string;
  type: string;
  url: string;
  robots: string | null;
}

/**
 * Applies a full, self-contained set of head values so metadata from a
 * previous route can never leak into the next one.
 */
function applySeo(options: ApplyOptions) {
  document.title = options.title;
  upsertCanonical(options.url);
  upsertMeta('name', 'description', options.description);
  setRobots(options.robots);

  upsertMeta('property', 'og:title', options.title);
  upsertMeta('property', 'og:description', options.description);
  upsertMeta('property', 'og:type', options.type);
  upsertMeta('property', 'og:url', options.url);
  upsertMeta('property', 'og:image', options.image);
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:locale', 'en_US');

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', options.title);
  upsertMeta('name', 'twitter:description', options.description);
  upsertMeta('name', 'twitter:image', options.image);
}

/**
 * Centralized route-aware head manager for runtime metadata
 * (title, description, canonical, Open Graph, Twitter Card).
 */
export default function useRouteMeta(pathname: string) {
  useEffect(() => {
    const data = routeSeo[pathname];
    const url = absoluteUrl(pathname);

    if (!data) {
      applySeo({
        title: notFoundSeo.title,
        description: notFoundSeo.description,
        image: SITE_LOGO,
        type: 'website',
        url,
        robots: 'noindex, follow',
      });
      return;
    }

    applySeo({
      title: data.title,
      description: data.description,
      image: data.image,
      type: data.type,
      url,
      robots: null,
    });
    upsertMeta('name', 'keywords', data.keywords);
  }, [pathname]);
}